import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, PAYMENT_LINK_STATUS_LABELS, type InvoiceSettlement, type PaymentLink, type PaymentLinkStatus, type PaymentProvider } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { useSession } from '../../session';

const NAMES: Record<PaymentProvider, string> = { stripe: 'Stripe (Karte, Lastschrift)', paypal: 'PayPal' };
const tone = (s: PaymentLinkStatus) => (s === 'paid' ? 'badge--won' : s === 'open' ? 'badge--in_progress' : '');
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

/**
 * Online bezahlen (KON-189): Zahlungslinks zu einer ausgestellten Rechnung mit dem Stripe- oder PayPal-Konto des Mandanten. Der Link bleibt gültig, bis die Rechnung bezahlt
 * ist, der Kunde bekommt ihn per E-Mail oder auf anderem Weg, und der Zahlungseingang erscheint von selbst in der Liste der Zahlungen.
 */
export function InvoicePaymentLinks({ invoice }: { invoice: { id: string; status: string; settlement: InvoiceSettlement | null } }) {
  const { can } = useSession();
  const client = useQueryClient();
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const key = ['invoice-out', invoice.id, 'payment-links'];
  const open = invoice.settlement?.openCents ?? 0;
  const list = useQuery({
    queryKey: key,
    queryFn: async () => await api<{ data: PaymentLink[]; meta: { providers: PaymentProvider[] } }>('GET', `/invoices-out/${invoice.id}/payment-links`),
    enabled: invoice.status === 'issued' && can('invoices_out.payments'),
  });
  const refresh = () => client.invalidateQueries({ queryKey: key });
  const create = useMutation({
    mutationFn: (provider: PaymentProvider) => api('POST', `/invoices-out/${invoice.id}/payment-links`, { provider }),
    onSuccess: () => {
      refresh();
      setMessage({ ok: true, text: 'Der Zahlungslink ist angelegt. Schicken Sie ihn dem Kunden oder kopieren Sie ihn.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Der Link konnte nicht angelegt werden.') }),
  });
  const send = useMutation({
    mutationFn: (id: string) => api<{ data: { sentTo: string } }>('POST', `/invoices-out/${invoice.id}/payment-links/${id}/send`, {}),
    onSuccess: (r) => setMessage({ ok: true, text: `Der Link ist per E-Mail an ${r.data.sentTo} unterwegs.` }),
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Die E-Mail konnte nicht gesendet werden.') }),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api('DELETE', `/invoices-out/${invoice.id}/payment-links/${id}`),
    onSuccess: () => {
      refresh();
      setMessage({ ok: true, text: 'Der Link ist zurückgezogen und lässt sich nicht mehr öffnen.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Der Link konnte nicht zurückgezogen werden.') }),
  });

  if (invoice.status !== 'issued' || !can('invoices_out.payments')) return null;
  const data = list.data;
  if (!data) return null;
  const providers = data.meta.providers;
  // Ohne verbundenen Dienst und ohne bisherige Links gibt es nichts zu zeigen
  if (providers.length === 0 && data.data.length === 0) return null;

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setMessage({ ok: true, text: 'Der Link ist in der Zwischenablage.' });
    } catch {
      setMessage({ ok: false, text: 'Das Kopieren hat nicht geklappt. Markieren Sie den Link und kopieren Sie ihn von Hand.' });
    }
  }

  return (
    <CollapsibleCard title="Online bezahlen" storageKey="invoice-payment-links">
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      {open > 0 ? (
        <>
          <p className="muted small">Der Kunde bezahlt die noch offenen {formatCents(open)} über einen Link. Die Zahlung wird automatisch zugeordnet.</p>
          <div className="form-actions">
            {providers.map((p) => (
              <button key={p} className="btn btn--secondary" type="button" disabled={create.isPending} onClick={() => { setMessage(null); create.mutate(p); }}>
                Link mit {NAMES[p]} anlegen
              </button>
            ))}
          </div>
        </>
      ) : (
        <p className="muted small">Die Rechnung ist vollständig bezahlt.</p>
      )}
      {data.data.length > 0 && (
        <table className="table table--cards">
          <thead>
            <tr><th>Dienst</th><th>Betrag</th><th>Status</th><th>Link</th><th><span className="sr-only">Aktionen</span></th></tr>
          </thead>
          <tbody>
            {data.data.map((l) => (
              <tr key={l.id}>
                <td data-label="Dienst">{NAMES[l.provider]}</td>
                <td data-label="Betrag">{formatCents(l.amountCents)}</td>
                <td data-label="Status"><span className={`badge ${tone(l.status)}`}>{PAYMENT_LINK_STATUS_LABELS[l.status]}</span></td>
                <td data-label="Link"><input className="input" readOnly value={l.url} aria-label="Zahlungslink" onFocus={(e) => e.target.select()} /></td>
                <td data-label="Aktionen">
                  {l.status === 'open' && (
                    <div className="form-actions">
                      <button className="btn btn--secondary btn--sm" type="button" onClick={() => void copy(l.url)}>Kopieren</button>
                      <button className="btn btn--secondary btn--sm" type="button" disabled={send.isPending} onClick={() => { setMessage(null); send.mutate(l.id); }}>Per E-Mail senden</button>
                      <button className="btn btn--secondary btn--sm" type="button" disabled={cancel.isPending} onClick={() => { if (window.confirm('Den Link zurückziehen? Der Kunde kann damit nicht mehr bezahlen.')) cancel.mutate(l.id); }}>Zurückziehen</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </CollapsibleCard>
  );
}
